import {
  bigint,
  boolean,
  foreignKey,
  index,
  pgEnum,
  pgTable,
  uuid,
  varchar,
  date,
} from 'drizzle-orm/pg-core';
import { Decimal } from 'decimal.js';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { decimal } from '@trashlab/api/src/domains/core/schemas/base-template';
import { sql } from 'drizzle-orm';

const fuel_surcharge_type = pgEnum('surcharge_type', ['FLAT', 'PERCENTAGE']);
const fuel_surcharge_charge_trigger = pgEnum('price_charge_trigger', [
  'ON_ORDER_PAYMENT_POLICY',
  'ON_DELIVERY_WO',
  'ON_REMOVAL_WO',
  'ON_SERVICE_WO',
  'ON_SERVICE_WO_NON_RECURRING',
  'ON_LIVELOAD_WO',
  'ON_SWAP_WO',
  'ON_DUMP_AND_RETURN_WO',
  'ON_RESPOT_WO',
  'ON_MIGRATE_WO',
  'RECURRING',
]);
const fuel_surcharge_calculation_base = pgEnum('price_calculation_base', [
  'TOTAL_ORDER_AMOUNT',
  'DUMPSTER',
  'PICKUP',
  'DISTANCE_BASED_FEE',
  'DELIVERY',
  'ON_DEMAND_SERVICE',
  'SURCHARGE',
  'RECURRING',
  'ADDITIONAL_DAYS',
  'DRY_RUN',
  'ADDITIONAL_TONS',
  'MIN_TONNAGE_FEE',
  'RESPOT',
]);

export const fuel_surcharge = pgTable(
  'fuel_surcharge',
  {
    ...defaultFields.fieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    name: varchar('name', { length: 255 }).notNull(),
    surchargeType: fuel_surcharge_type('surchargeType').notNull(),
    chargeFee: bigint('chargeFee', { mode: 'number' }).default(0).notNull(),
    chargePercentage: decimal('chargePercentage', { precision: 7, scale: 4 })
      .default(new Decimal(0))
      .notNull(),
    taxable: boolean('taxable').default(false).notNull(),
    qbItemId: varchar('qbItemId', { length: 255 }),
    qbClassId: varchar('qbClassId', { length: 255 }),
    chargeTriggers: fuel_surcharge_charge_trigger('chargeTriggers')
      .array()
      .notNull()
      .default(sql`ARRAY[]::price_charge_trigger[]`),
    calculationBase: fuel_surcharge_calculation_base('calculationBase')
      .array()
      .notNull()
      .default(sql`ARRAY[]::price_calculation_base[]`),
    excludeCredits: boolean('excludeCredits').default(false).notNull(),
  },
  (table) => ({
    createdAtTs_idx: index('fuel_surcharge_createdAtTs_idx').on(
      table.createdAtTs,
    ),
    updatedAtTs_idx: index('fuel_surcharge_updatedAtTs_idx').on(
      table.updatedAtTs,
    ),
    updatedAtTs_desc_idx: index('fuel_surcharge_updatedAtTs_desc_idx').on(
      table.updatedAtTs.desc(),
    ),
    versionNumber_idx: index('fuel_surcharge_versionNumber_idx').on(
      table.versionNumber,
    ),
    tenantId_name_idx: index('fuel_surcharge_tenantId_name_idx').on(
      table.tenantId,
      table.name,
    ),
  }),
);

export const fuel_surcharge_history = pgTable('fuel_surcharge_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  lastModifiedBy: uuid('lastModifiedBy'),
  createdBy: uuid('createdBy'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  name: varchar('name', { length: 255 }),
  surchargeType: fuel_surcharge_type('surchargeType'),
  chargeFee: bigint('chargeFee', { mode: 'number' }),
  chargePercentage: decimal('chargePercentage', { precision: 7, scale: 4 }),
  taxable: boolean('taxable'),
  qbItemId: varchar('qbItemId', { length: 255 }),
  qbClassId: varchar('qbClassId', { length: 255 }),
  chargeTriggers: fuel_surcharge_charge_trigger('chargeTriggers').array(),
  calculationBase: fuel_surcharge_calculation_base('calculationBase').array(),
  excludeCredits: boolean('excludeCredits'),
});

export const fuel_surcharge_rate = pgTable(
  'fuel_surcharge_rate',
  {
    ...defaultFields.fieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    fuelSurchargeId: uuid('fuelSurchargeId').notNull(),
    startDate: date('startDate', { mode: 'string' }).notNull(),
    endDate: date('endDate', { mode: 'string' }),
    chargeFee: bigint('chargeFee', { mode: 'number' }).default(0).notNull(),
    chargePercentage: decimal('chargePercentage', { precision: 7, scale: 4 })
      .default(new Decimal(0))
      .notNull(),
  },
  (table) => ({
    createdAtTs_idx: index('fuel_surcharge_rate_createdAtTs_idx').on(
      table.createdAtTs,
    ),
    tenantId_idx: index('fuel_surcharge_rate_tenantId_idx').on(table.tenantId),
    updatedAtTs_idx: index('fuel_surcharge_rate_updatedAtTs_idx').on(
      table.updatedAtTs,
    ),
    updatedAtTs_desc_idx: index('fuel_surcharge_rate_updatedAtTs_desc_idx').on(
      table.updatedAtTs.desc(),
    ),
    versionNumber_idx: index('fuel_surcharge_rate_versionNumber_idx').on(
      table.versionNumber,
    ),
    fuelSurchargeId_startDate_idx: index(
      'fuel_surcharge_rate_fuelSurchargeId_startDate_idx',
    ).on(table.fuelSurchargeId, table.startDate),
    fuel_surcharge_rate_fuelSurchargeId_fkey: foreignKey({
      columns: [table.fuelSurchargeId],
      foreignColumns: [fuel_surcharge.id],
      name: 'fuel_surcharge_rate_fuelSurchargeId_fkey',
    }),
  }),
);

export const fuel_surcharge_rate_history = pgTable(
  'fuel_surcharge_rate_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    fuelSurchargeId: uuid('fuelSurchargeId'),
    startDate: date('startDate', { mode: 'string' }),
    endDate: date('endDate', { mode: 'string' }),
    chargeFee: bigint('chargeFee', { mode: 'number' }),
    chargePercentage: decimal('chargePercentage', { precision: 7, scale: 4 }),
  },
);
